import type { MisconceptionDefinition } from './types'

/**
 * GELD — Geld misconcepties.
 *
 * Audit §10.7.13 (mei 2026): TGL-GELD-* prefixes verwijderd om te matchen met
 * kale GELD-* familyIds in seeds/LDG na T8 Phase 2 rename (april 2026).
 */
export const GELD_MISCONCEPTIONS: MisconceptionDefinition[] = [
  {
    id: 'geld.decimal-comma',
    label: 'Komma vergeten/verkeerd',
    module: 'Geld',
    description:
      'Leerling plaatst de decimale komma verkeerd bij geldbedragen.',
    typicalFamilies: ['GELD-GELD-KOMMA'],
  },
  {
    id: 'geld.change-calculation',
    label: 'Wisselgeld verkeerd berekend',
    module: 'Geld',
    description:
      'Leerling berekent wisselgeld verkeerd (trekt van het verkeerde bedrag af).',
    typicalFamilies: ['GELD-GELD-TERUG', 'GELD-GELD-TERUGREKENEN'],
  },
  {
    id: 'geld.total-vs-unit-price',
    label: 'Totaalprijs verward met eenheidsprijs',
    module: 'Geld',
    description:
      'Leerling vergelijkt totaalprijzen in plaats van de prijs per eenheid (per stuk, per kg, per liter).',
    typicalFamilies: ['GELD-GELD-KOOPJES', 'GELD-GELD-PRIJS-PER'],
  },
  {
    id: 'geld.wrong-division-direction',
    label: 'Verkeerde deelrichting bij eenheidsprijs',
    module: 'Geld',
    description:
      'Leerling deelt het aantal door de prijs in plaats van de prijs door het aantal.',
    typicalFamilies: ['GELD-GELD-KOOPJES', 'GELD-GELD-PRIJS-PER'],
  },
  {
    id: 'geld.surcharge-is-answer',
    label: 'Verwart opslag met nieuwe prijs',
    module: 'Geld',
    description:
      'Leerling geeft het opslagbedrag als antwoord in plaats van de nieuwe prijs (of omgekeerd).',
    typicalFamilies: ['GELD-GELD-KORTING'],
  },
  {
    id: 'geld.percent-equals-amount',
    label: 'Denkt dat percentage gelijk is aan bedrag',
    module: 'Geld',
    description:
      'Leerling denkt dat bijv. 25% korting altijd €25 is, ongeacht het bedrag.',
    typicalFamilies: ['GELD-GELD-KORTING'],
  },
  {
    id: 'geld.add-instead-of-subtract',
    label: 'Telt korting op in plaats van af te trekken',
    module: 'Geld',
    description:
      'Leerling telt het kortingsbedrag bij de prijs op in plaats van het af te trekken.',
    typicalFamilies: ['GELD-GELD-KORTING'],
  },
  {
    id: 'geld.confuses-discount-with-new-price',
    label: 'Verwart nieuwe prijs met kortingsbedrag',
    module: 'Geld',
    description:
      'Leerling geeft de nieuwe prijs als het kortingsbedrag of omgekeerd.',
    typicalFamilies: ['GELD-GELD-KORTING'],
  },
  {
    id: 'geld.wrong-percentage-calc',
    label: 'Berekent percentage verkeerd',
    module: 'Geld',
    description:
      'Leerling maakt een rekenfout bij het berekenen van het percentage van een bedrag.',
    typicalFamilies: ['GELD-GELD-KORTING'],
  },
  {
    id: 'geld.subtract-instead-of-add',
    label: 'Trekt opslag af in plaats van op te tellen',
    module: 'Geld',
    description:
      'Leerling trekt het opslagbedrag af van de prijs in plaats van het erbij op te tellen.',
    typicalFamilies: ['GELD-GELD-KORTING'],
  },
  {
    id: 'geld.deling-te-laag',
    label: 'Deelt te laag uit bij spaarberekening',
    module: 'Geld',
    description:
      'Leerling deelt het spaardoel door een te laag getal of maakt een delingsfout waardoor het resultaat te laag is.',
    typicalFamilies: ['GELD-GELD-SPAREN'],
  },
  {
    id: 'geld.deling-te-hoog',
    label: 'Deelt te hoog uit bij spaarberekening',
    module: 'Geld',
    description:
      'Leerling deelt het spaardoel door een te hoog getal of maakt een delingsfout waardoor het resultaat te hoog is.',
    typicalFamilies: ['GELD-GELD-SPAREN'],
  },
  {
    id: 'geld.verkeerd-deeltal',
    label: 'Gebruikt verkeerd deeltal bij sparen',
    module: 'Geld',
    description:
      'Leerling deelt door het verkeerde getal (bijv. door het spaardoel i.p.v. het weekbedrag).',
    typicalFamilies: ['GELD-GELD-SPAREN'],
  },
  {
    id: 'geld.cent-optelling-fout',
    label: 'Telt centen verkeerd op',
    module: 'Geld',
    description:
      'Leerling maakt een fout bij het optellen van centen (bijv. 50+75=100 i.p.v. 125).',
    typicalFamilies: ['GELD-GELD-BUDGET'],
  },
  {
    id: 'geld.uitgaven-als-wisselgeld',
    label: 'Verwart totale uitgaven met wisselgeld',
    module: 'Geld',
    description:
      'Leerling geeft het totaal van de uitgaven als wisselgeld i.p.v. budget minus uitgaven.',
    typicalFamilies: ['GELD-GELD-BUDGET'],
  },
  {
    id: 'geld.aftrekken-vergeten',
    label: 'Maakt aftrekfout bij centen',
    module: 'Geld',
    description:
      'Leerling maakt een fout bij het aftrekken van centenbedragen.',
    typicalFamilies: ['GELD-GELD-BUDGET'],
  },
  {
    id: 'geld.aftrekfout-tientallen',
    label: 'Maakt aftrekfout bij tientallen',
    module: 'Geld',
    description:
      'Leerling maakt een fout bij het aftrekken van tientallen (bijv. 150-85=75 i.p.v. 65).',
    typicalFamilies: ['GELD-GELD-BUDGET'],
  },
  {
    id: 'geld.aftrekfout-eenheden',
    label: 'Maakt aftrekfout bij eenheden',
    module: 'Geld',
    description: 'Leerling maakt een fout bij het aftrekken van eenheden.',
    typicalFamilies: ['GELD-GELD-BUDGET'],
  },
  {
    id: 'geld.optellen-ipv-aftrekken',
    label: 'Telt op in plaats van af te trekken',
    module: 'Geld',
    description:
      'Leerling telt bedragen op terwijl aftrekken gevraagd wordt (bijv. bij "hoeveel nog sparen").',
    typicalFamilies: ['GELD-GELD-BUDGET'],
  },
  {
    id: 'geld.kosten-hoger-schatten',
    label: 'Schat kosten hoger in dan ze zijn',
    module: 'Geld',
    description:
      'Leerling denkt dat de kosten het budget overschrijden terwijl dat niet zo is.',
    typicalFamilies: ['GELD-GELD-BUDGET'],
  },
  {
    id: 'geld.precies-genoeg-verwarring',
    label: 'Verwart bijna genoeg met precies genoeg',
    module: 'Geld',
    description:
      'Leerling denkt dat het budget precies genoeg is terwijl er geld overblijft of tekort is.',
    typicalFamilies: ['GELD-GELD-BUDGET'],
  },
  {
    id: 'geld.aftrekken-ipv-delen',
    label: 'Trekt af in plaats van te delen',
    module: 'Geld',
    description:
      'Leerling trekt het weekbedrag af van het spaardoel i.p.v. te delen (bijv. €35-€5=€30 i.p.v. €35÷€5=7).',
    typicalFamilies: ['GELD-GELD-BUDGET'],
  },
  {
    id: 'geld.percentage-van-nieuwe-prijs',
    label: 'Berekent percentage van de nieuwe prijs i.p.v. de oude',
    module: 'Geld',
    description:
      'Leerling berekent het percentage van de nieuwe prijs en telt dat erbij op, in plaats van terug te rekenen naar de oude prijs.',
    typicalFamilies: ['GELD-GELD-TERUGREKENEN'],
  },
  {
    id: 'geld.dubbele-korting',
    label: 'Past korting dubbel toe',
    module: 'Geld',
    description:
      'Leerling trekt de korting er nog een keer af van de al-gereduceerde prijs.',
    typicalFamilies: ['GELD-GELD-TERUGREKENEN'],
  },
  {
    id: 'geld.percentage-als-vast-bedrag',
    label: 'Behandelt percentage als vast bedrag',
    module: 'Geld',
    description:
      'Leerling telt het percentagegetal op als vast bedrag (bijv. 20% = €20).',
    typicalFamilies: ['GELD-GELD-TERUGREKENEN'],
  },
  {
    id: 'geld.dubbele-verhoging',
    label: 'Past verhoging dubbel toe',
    module: 'Geld',
    description:
      'Leerling telt de verhoging er nog een keer bij op de al-verhoogde prijs.',
    typicalFamilies: ['GELD-GELD-TERUGREKENEN'],
  },
  {
    id: 'geld.verkeerd-deelpercentage',
    label: 'Deelt door verkeerd percentage',
    module: 'Geld',
    description:
      'Leerling deelt door het verkeerde percentage bij terugrekenen (bijv. door 0,60 i.p.v. 0,75 bij 25% korting).',
    typicalFamilies: ['GELD-GELD-TERUGREKENEN'],
  },

  // ─── BTW misconcepties (GELD-BTW) ─────────────────────────────────────
  // Migrated mei 2026 from uppercase ad-hoc IDs to canonical geld.btw-* prefix.
  {
    id: 'geld.btw-only-tax',
    label: 'Geeft alleen BTW-bedrag i.p.v. totaalprijs',
    module: 'Geld',
    description:
      'Leerling geeft alleen het BTW-bedrag terug in plaats van de totaalprijs inclusief BTW.',
    typicalFamilies: ['GELD-BTW'],
  },
  {
    id: 'geld.btw-subtract',
    label: 'Trekt BTW af i.p.v. op te tellen',
    module: 'Geld',
    description:
      'Leerling trekt het BTW-bedrag van de prijs af in plaats van het erbij op te tellen.',
    typicalFamilies: ['GELD-BTW'],
  },
  {
    id: 'geld.btw-literal-cents',
    label: 'Interpreteert BTW-percentage als centen',
    module: 'Geld',
    description:
      'Leerling interpreteert 21% letterlijk als €0,21 in plaats van als verhouding 0,21 × prijs.',
    typicalFamilies: ['GELD-BTW'],
  },
  {
    id: 'geld.btw-flat-rate',
    label: 'Behandelt BTW-percentage als vast bedrag',
    module: 'Geld',
    description:
      'Leerling denkt dat 21% altijd €21 is ongeacht het bedrag (percentage als vast bedrag).',
    typicalFamilies: ['GELD-BTW'],
  },
  {
    id: 'geld.btw-subtract-from-incl',
    label: 'Trekt BTW af i.p.v. te delen door 1,XX',
    module: 'Geld',
    description:
      'Bij terugrekenen van inclusief naar exclusief BTW trekt de leerling het percentage van de inclusief-prijs af i.p.v. te delen door 1,21 (of 1,09).',
    typicalFamilies: ['GELD-BTW'],
  },
  {
    id: 'geld.btw-add-instead',
    label: 'Telt BTW erbij i.p.v. eruit te rekenen',
    module: 'Geld',
    description:
      'Leerling telt BTW erbij op terwijl er teruggerekend moet worden naar exclusief-prijs.',
    typicalFamilies: ['GELD-BTW'],
  },
  {
    id: 'geld.btw-subtract-flat',
    label: 'Trekt percentagegetal af als vast bedrag',
    module: 'Geld',
    description:
      'Leerling trekt €21 (of €9) als vast bedrag af bij BTW-aftrek i.p.v. 21% (of 9%) van het bedrag.',
    typicalFamilies: ['GELD-BTW'],
  },
  {
    id: 'geld.btw-confuse-remainder',
    label: 'Verwart BTW-bedrag met prijs-na-aftrek',
    module: 'Geld',
    description:
      'Leerling geeft de prijs na BTW-aftrek terug in plaats van alleen het BTW-bedrag.',
    typicalFamilies: ['GELD-BTW'],
  },
  {
    id: 'geld.btw-confuse-total',
    label: 'Verwart BTW-bedrag met totaalprijs',
    module: 'Geld',
    description:
      'Leerling geeft de totaalprijs inclusief BTW terug in plaats van alleen het BTW-bedrag.',
    typicalFamilies: ['GELD-BTW'],
  },
  {
    id: 'geld.btw-wrong-subtraction',
    label: 'Trekt willekeurig bedrag af bij BTW',
    module: 'Geld',
    description:
      'Leerling trekt een willekeurig of niet-gerelateerd bedrag af bij BTW-berekening.',
    typicalFamilies: ['GELD-BTW'],
  },

  // ─── WISSEL misconcepties (GELD-WISSELEN) ─────────────────────────────
  // Migrated mei 2026 from uppercase ad-hoc IDs to canonical geld.wissel-* prefix.
  {
    id: 'geld.wissel-no-conversion',
    label: 'Negeert wisselkoers (1:1)',
    module: 'Geld',
    description:
      'Leerling denkt dat 1 euro = 1 dollar (of pond/yen) en past de wisselkoers niet toe.',
    typicalFamilies: ['GELD-WISSEL', 'GELD-WISSELEN'],
  },
  {
    id: 'geld.wissel-divide-instead',
    label: 'Deelt i.p.v. vermenigvuldigt bij wisselkoers',
    module: 'Geld',
    description:
      'Leerling deelt door de wisselkoers terwijl vermenigvuldigen nodig is (of omgekeerd).',
    typicalFamilies: ['GELD-WISSEL', 'GELD-WISSELEN'],
  },
  {
    id: 'geld.wissel-multiply-instead',
    label: 'Vermenigvuldigt i.p.v. deelt bij wisselkoers',
    module: 'Geld',
    description:
      'Leerling vermenigvuldigt met de wisselkoers terwijl delen nodig is (terugconversie naar euro).',
    typicalFamilies: ['GELD-WISSEL', 'GELD-WISSELEN'],
  },
  {
    id: 'geld.wissel-add-rate',
    label: 'Telt wisselkoers op i.p.v. vermenigvuldigt',
    module: 'Geld',
    description:
      'Leerling telt de wisselkoers op bij het bedrag in plaats van te vermenigvuldigen.',
    typicalFamilies: ['GELD-WISSEL', 'GELD-WISSELEN'],
  },
  {
    id: 'geld.wissel-wrong-divisor',
    label: 'Gebruikt verkeerde grootteorde deler',
    module: 'Geld',
    description:
      'Leerling deelt door verkeerde grootteorde (bijv. 13 i.p.v. 130 yen per euro).',
    typicalFamilies: ['GELD-WISSEL', 'GELD-WISSELEN'],
  },
  {
    id: 'geld.wissel-divide-by-100',
    label: 'Deelt door 100 i.p.v. wisselkoers',
    module: 'Geld',
    description:
      'Leerling deelt automatisch door 100 (percentage-reflex) in plaats van door de wisselkoers.',
    typicalFamilies: ['GELD-WISSEL', 'GELD-WISSELEN'],
  },
  {
    id: 'geld.wissel-rounding-error',
    label: 'Rondt te vroeg of verkeerd af bij wisselkoers',
    module: 'Geld',
    description:
      'Leerling rondt tussenuitkomsten te vroeg af of in de verkeerde richting bij wisselkoers-berekening.',
    typicalFamilies: ['GELD-WISSEL', 'GELD-WISSELEN'],
  },
  {
    id: 'geld.wissel-wrong-rate',
    label: 'Gebruikt verkeerde wisselkoers',
    module: 'Geld',
    description:
      'Leerling gebruikt de verkeerde wisselkoers (bijv. 1,20 i.p.v. 1,10) terwijl de juiste in de vraag staat.',
    typicalFamilies: ['GELD-WISSEL', 'GELD-WISSELEN'],
  },
]
